import Icon from '@tiger/icons';
import moment from 'moment';
import { FC, useMemo } from 'react';
import { FormControl } from 'react-bootstrap';
import { $Formutil, EasyField, Form } from 'react-bootstrap-formutil';
import FormGroup from 'app/components/FormGroup';
import Birthday from 'app/components/forms/PersonalInfo/components/Birthday';
import FormSelect, { FormSelectProps } from 'app/components/FormSelect';
import StickyBox from 'app/components/StickyBox';
import classlist from 'app/utils/classlist';
import { printf } from 'app/utils/i18n';
import Button from 'components/base/Button';
import Modal, { ModalHandler } from 'components/base/Modal';
import Debug from 'components/Debug';
import RealNameInfo from 'components/forms/RealNameInfo';
import TaxCountry from 'components/forms/TaxCountry';
import TaxNumber from 'components/forms/TaxNumber';
import Toast from 'components/Toast';
import { Fade } from 'components/Transition';
import { BeneficiaryRelationshipEnum, BeneficiaryTypeEnum, NameTypeEnum } from 'types/customer';
import { isEmptyValue } from 'utils/helper';
import patterns from 'utils/patterns';
import { BeneficiaryDataItem } from '../type';
import styles from './style.scss';

interface ContentProps {
    /**
     * 再做数据比对的时候, 需要过滤掉当前元素在进行比较, 所以这里需要传入当前元素, 在当前beneficiaryInfo下的index
     * 但是只有编辑信息的时候才需要过滤, 新增弹窗不需要过滤, 所以是可选,在会有编辑的时候传入即可
     */
    filterIndex?: number;
    defaultValues?: BeneficiaryDataItem;
    primaryBeneficiaryInfo: BeneficiaryDataItem[]; // 主受益人信息
    secondaryBeneficiaryInfo: BeneficiaryDataItem[]; // 次受益人信息
    beneficiaryType: BeneficiaryTypeEnum;
    // 开第一个户的时候的需要比对一下, 受益人的税号不能跟账户主人的税号一样, 所以这里要传入账户主人的税号
    masterTaxNumber?: string;
}

const relationshipOptions: FormSelectProps['options'] = [
    { value: BeneficiaryRelationshipEnum.SPOUSE, label: __('Spouse') },
    { value: BeneficiaryRelationshipEnum.SON, label: __('Son') },
    { value: BeneficiaryRelationshipEnum.DAUGHTER, label: __('Daughter') },
    { value: BeneficiaryRelationshipEnum.FATHER, label: __('Father') },
    { value: BeneficiaryRelationshipEnum.MOTHER, label: __('Mother') },
    { value: BeneficiaryRelationshipEnum.BROTHER, label: __('Brother') },
    { value: BeneficiaryRelationshipEnum.SISTER, label: __('Sister') },
    { value: BeneficiaryRelationshipEnum.OTHER, label: __('Other') }
];
const Content: FC<ContentProps & ModalHandler> = ({
    close,
    dismiss,
    defaultValues,
    primaryBeneficiaryInfo,
    secondaryBeneficiaryInfo,
    beneficiaryType,
    filterIndex,
    masterTaxNumber
}) => {
    const filterBeneficiaryInfo = useMemo(() => {
        // 如果没有传filterIndex, 则直接返回主受益人和次受益人的结合数据
        if (typeof filterIndex === 'undefined') {
            return primaryBeneficiaryInfo.concat(secondaryBeneficiaryInfo);
        }

        // 判断不能重复的数据的时候, 需要把自身数据过滤掉
        if (beneficiaryType === BeneficiaryTypeEnum.PRIMARY) {
            return primaryBeneficiaryInfo.filter((_, i) => i !== filterIndex).concat(secondaryBeneficiaryInfo);
        }

        return primaryBeneficiaryInfo.concat(secondaryBeneficiaryInfo.filter((_, i) => i !== filterIndex));
    }, [beneficiaryType, primaryBeneficiaryInfo, secondaryBeneficiaryInfo, filterIndex]);

    return (
        <Form
            $defaultValues={defaultValues}
            render={($formutil: $Formutil<BeneficiaryDataItem>) => {
                const { beneficiary_relationship, mask_tax_identification_number, tax_residence_country } =
                    $formutil.$params;

                const handleSubmit = (ev: React.FormEvent) => {
                    ev.preventDefault();

                    const { $params, $invalid, $batchState, $getFirstError } = $formutil;

                    if ($invalid) {
                        $batchState({
                            $dirty: true,
                            $touched: true
                        });

                        Toast.show($getFirstError());
                    } else {
                        close($params);
                    }
                };

                return (
                    <form noValidate onSubmit={handleSubmit}>
                        <div className={styles.header}>
                            <h4>
                                {beneficiaryType === BeneficiaryTypeEnum.PRIMARY
                                    ? __('Primary Beneficiary')
                                    : __('Contingent Beneficiary')}
                            </h4>
                            <Icon type="close" onClick={() => dismiss()} />
                        </div>
                        <div>
                            <EasyField type="hidden" name="beneficiary_type" $defaultValue={beneficiaryType} />
                            {/* 受益人信息里面的姓名类型要固定死是USA */}
                            <RealNameInfo nameType={NameTypeEnum.USA} />
                            <FormGroup
                                name="beneficiary_relationship"
                                label={__('Relationship')}
                                checker={value => {
                                    if (isEmptyValue(value)) {
                                        return printf(__('Please select your %s'), __('Relationship'));
                                    }

                                    if (
                                        value === BeneficiaryRelationshipEnum.SPOUSE &&
                                        filterBeneficiaryInfo?.some(
                                            i => i.beneficiary_relationship === BeneficiaryRelationshipEnum.SPOUSE
                                        )
                                    ) {
                                        return __('您已经选择配偶作为受益人');
                                    }

                                    return true;
                                }}>
                                <FormSelect placeholder={__('Relationship')} options={relationshipOptions} />
                            </FormGroup>
                            <Fade in={beneficiary_relationship === BeneficiaryRelationshipEnum.OTHER} exit={false}>
                                <FormGroup
                                    name="relationship_describe"
                                    label={__('关系名称')}
                                    checker={value => {
                                        if (isEmptyValue(value)) {
                                            return printf(__('Please enter the %s'), __('关系名称'));
                                        }

                                        return true;
                                    }}>
                                    <FormControl placeholder={__('关系名称')} />
                                </FormGroup>
                            </Fade>
                            {/* 受益人跟开户需要填写的生日不一样, 时间不需要限制到18岁, 可以最大选择到当天 */}
                            <Birthday maxDate={moment()} />
                            {/* 受益人增加纳税国和税号选项，默认为美国。https://tigertech.feishu.cn/wiki/H61iwvjMdivJUnkbEkrcI5Ftned */}
                            <TaxCountry isSSN $defaultValue="USA" $onFieldChange={() => $formutil.$validates()} />
                            <TaxNumber
                                maskTaxIdentificationNumber={mask_tax_identification_number}
                                checker={value => {
                                    // 如果跟账户主人的税号一样, 或者 同一种ira类型的税号有重复
                                    if (
                                        masterTaxNumber === value ||
                                        filterBeneficiaryInfo?.some(i => i.tax_identification_number === value)
                                    ) {
                                        return __('The tax identification number has been used');
                                    }

                                    if (tax_residence_country === 'USA' && !/^\d{9}$/g.test(value)) {
                                        return __('Tax identification number should be a nine-digit number');
                                    }

                                    return true;
                                }}
                            />
                            <FormGroup
                                name="percentage"
                                label={__('Percentage')}
                                addons={{ end: '%' }}
                                checker={value => {
                                    if (isEmptyValue(value)) {
                                        return printf(__('Please enter your %s'), __('Percentage'));
                                    }

                                    // 先判断了是否是数字, 然后转化成数字判断是否是正整数, 最后判断是否在0-100之间
                                    if (
                                        !patterns.isNumber(value) ||
                                        !Number.isInteger(Number(value)) ||
                                        Number(value) <= 0 ||
                                        Number(value) > 100
                                    ) {
                                        return __('请输入正整数(最终占比总和需要等于100%)');
                                    }

                                    return true;
                                }}>
                                <FormControl placeholder={__('Percentage')} />
                            </FormGroup>
                        </div>
                        <StickyBox>
                            <div className={styles.btnContainer}>
                                <div className={styles.btnPlaceholder} />
                                <Button
                                    className={classlist('mt-3', styles.btn, styles.cancelBtn)}
                                    type="dark"
                                    ghost
                                    onClick={() => dismiss()}
                                    size="lg"
                                    block>
                                    {__('Cancel')}
                                </Button>
                                <Button
                                    className={classlist('mt-3', styles.btn)}
                                    type="primary"
                                    htmlType="submit"
                                    size="lg"
                                    block>
                                    {__('Save')}
                                </Button>
                            </div>
                        </StickyBox>
                        <Debug data={$formutil.$params} />
                        <Debug data={$formutil.$errors} />
                    </form>
                );
            }}
        />
    );
};

export function openBeneficiaryModal(params: ContentProps) {
    return Modal.openSlideModal({
        component: props => <Content {...params} {...props} />,
        responsive: true,
        backdrop: 'static',
        dialogClassName: styles.modalDialog,
        containerClassName: styles.modalContainer
    });
}
